/**
 * View Component: BatchActionBar
 * T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」
 *
 * 改进清单原文:「长按多选 + 批量复制/收藏」。
 *
 * 之前用户要处理 10 篇内容,只能:
 *   1) 一篇一篇点收藏(10 次点击)
 *   2) 一篇一篇打开详情复制(10 次打开 + 10 次复制)
 * 没有「这 10 篇我都要」的一次性入口。
 *
 * 本组件是长按进入多选态后浮出的操作条:
 *   · 显示已选数量
 *   · 「复制」—— 把选中的 N 篇组装成一段文案,一次写进剪贴板
 *     (口径与 PostDetail 的单篇复制完全一致,见 harness/postText.js)
 *   · 「收藏」—— 把选中的 N 篇**设成**已收藏(不是逐个 toggle,
 *     否则本来就收藏过的那篇会被反向取消)
 *   · 「取消收藏」—— 把选中的 N 篇**取消**已收藏
 *   · 「取消」—— 退出多选态,清空选择
 *
 * 位置与 FavoriteBar 区分:
 *   FavoriteBar = 「已选素材清单」(持久收藏,常驻底部)
 *   BatchActionBar = 「本次批量操作」(临时选择,进入多选态才出现)
 *   两者语义不同 ⇒ 视觉上也必须区分(BatchActionBar 用强调色描边)。
 *
 * 无障碍:
 *   · role="region" + aria-label,读屏用户知道这是一块批量操作区
 *   · 每个按钮都有具体 aria-label(不是光秃秃的「复制」)
 *   · 结果通过 role="status" aria-live="polite" 播报
 */

// NOTE: 不要显式 `import React from 'react'` —— vite.config.js 的
// esbuild.jsxInject 已经给每个 .jsx 注入 React,重复 import 会报
// "Identifier \"React\" has already been declared" 并让整轮构建失败。
// T-Cron-2026-10-31 08:00:toast 收口到 harness/DismissibleToast.jsx
// (点击 / Esc 关闭 + 键盘可达),4 个 toast 入口行为一致。
import { DismissibleToast } from '../harness/DismissibleToast.jsx';

/**
 * 🔴 T-Cron-2026-11-01 03:30 (T128) — 把操作条的真实高度发布成 CSS 变量。
 *
 * 【为什么需要它】
 *   操作条在移动端是「两行/三行不定」:
 *     w=320 → 按钮换 3 行 ⇒ 实测高 136px
 *     w=375 → 两行           ⇒ 实测高  92px
 *   而 FAB 必须**停在操作条顶边之上** ⇒ 它需要知道这个高度。
 *   写死 `bottom: 168px` 只在 375px 成立,320px 会留一条 28px 的空隙,
 *   260px 之类的更窄机型又会被截断 ⇒ **写死数值必挂**。
 *
 * 【做法】ResizeObserver 观察操作条自身高度,写进
 *   `document.documentElement.style.setProperty('--batch-bar-h', px)`。
 *   ⇒ 任何字体缩放 / 按钮文案变化 / 窗口旋转都自动跟随。
 *   ⇒ FAB 的 CSS 只读这个变量 ⇒ 两边永远一致。
 *
 * 【🔴 为什么必须用 callback ref,不能用 useRef + useEffect】
 *   BatchActionBar 在 `!count && !active` 时**返回 null** —— 组件挂载了,
 *   但那个 div 还不存在 ⇒ `ref.current === null`。
 *   如果用 `useEffect(() => {...}, [ref])`:
 *     · effect 在挂载时跑一次,那时 ref.current 是 null ⇒ 直接 return;
 *     · 依赖数组 `[ref]` 里是 **ref 对象本身**,它在所有 render 之间
 *       身份恒定 ⇒ **effect 永远不会重跑**;
 *     · 于是用户点「多选」让 div 真正挂上时,变量**从来没被写进去**。
 *   ⇒ 实测症状:`--batch-bar-h` 读出来是空串, FAB 一直用 fallback 92px。
 *     在 375px 上 fallback 恰好等于真实高度 ⇒ **看起来对了**,
 *     在 320px(真实 136px)上就会错 ⇒ 这类 bug 极难被发现。
 *   ⇒ callback ref 在 DOM 节点**真正挂载的那一刻**被调用,
 *     这才是「节点出现了」的正确信号。
 *
 * 【为什么用 ResizeObserver 而不是 window resize】
 *   操作条高度变化的原因**不止**窗口 resize(按钮文案变化、
 *   「下载中…」变长、选中数量数字变宽都会改高度),
 *   window resize 监听不到这些。
 */
function usePublishBatchBarHeight() {
  const observerRef = React.useRef(null);

  // 卸载时清理:变量必须随组件一起消失,否则 FAB 会一直按
  // 「操作条还在」的高度偏移(操作条没了、FAB 却留在上面)。
  React.useEffect(() => () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    document.documentElement.style.removeProperty('--batch-bar-h');
  }, []);

  // callback ref:DOM 节点挂载 ⇒ 立刻测量并开始观察。
  // 节点卸载时 React 会用 null 再调一次 ⇒ 断开观察 + 清变量。
  return React.useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    document.documentElement.style.removeProperty('--batch-bar-h');
    if (!el) return;

    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) {
        document.documentElement.style.setProperty('--batch-bar-h', `${h}px`);
      }
    };

    publish();
    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(publish);
      observerRef.current.observe(el);
    }
  }, []);
}

function BatchActionBar({ count, hiddenCount = 0, onCopy, onFavorite, onUnfavorite, onDownloadCovers, onCancel, onSelectAll, allSelected = false, visibleCount = 0, active = false, busy = false, downloading = false, progress = null }) {
  const [toast, setToast] = React.useState('');
  const toastTimer = React.useRef(null);
  // 🔴 T-Cron-2026-11-01 03:30 (T128):把操作条真实高度发布成
  // --batch-bar-h,供 FAB 让位计算使用(见 usePublishBatchBarHeight 注释)。
  // ⚠️ 必须是 callback ref —— 见该函数注释里「为什么不能用 useRef + useEffect」。
  const barRef = usePublishBatchBarHeight();

  const showToast = React.useCallback((msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2600);
  }, []);

  React.useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  // T-Cron-2026-10-24 03:30:原来是 `if (!count) return null;` —— 只有
  // 真的选中了卡片才显示操作条。
  //
  // 桌面端加了「多选」按钮之后,用户会先点按钮进入多选态,**此时还没选
  // 任何卡片** ⇒ 操作条不显示 ⇒ 用户看到的是「点了按钮但什么都没发生」,
  // 不知道该下一步点卡片。
  //
  // ⇒ 改成:多选态(active)就显示,哪怕 count===0;此时把动作按钮禁用,
  // 并给一句提示「点卡片选择」。选中第一张后恢复正常。
  if (!count && !active) return null;

  return (
    <>
      <div className="batch-bar" role="region" aria-label="批量操作" ref={barRef}>
        <div className="batch-bar-count" aria-live="polite">
          已选 <strong>{count}</strong> 篇
          {/* 还没选任何卡片时给一句下一步提示,否则用户不知道该点什么 */}
          {!count && !hiddenCount && <span className="batch-bar-hint">点卡片选择</span>}
          {/*
            🔴 T-Cron-2026-11-01 14:00 — 修「计数口径与执行口径脱节」(P1)。
            count 现在是**实际可操作数**(与按钮 aria-label、复制 toast
            里的数字完全一致);hiddenCount 是被当前筛选排除掉的选中数。
            ⇒ 用户切了筛选导致选中项全被筛走时,这一屏会显示
              「已选 0 篇 · 3 篇在当前筛选外」,
              动作按钮同时 disabled ⇒ **状态自洽**,不再是
              「写着已选 3 篇、按钮可点、点了没反应」。
            用 aria-live 播报,读屏用户切完筛选也知道发生了什么。 */}
          {hiddenCount > 0 && (
            <span className="batch-bar-hint batch-bar-hint-hidden">
              {hiddenCount} 篇在当前筛选外
            </span>
          )}
        </div>

        <div className="batch-bar-actions">
          {/* T-Cron-2026-10-31 02:00 早间 (T127) — 改进清单 #8「批量操作」补全。
              🔴 **真实功能缺口(不是锦上添花)**:快捷键 `a`(多选模式下
              全选 / 取消全选)在 harness/useKeyboardShortcuts.js 里实现,
              并在 App.jsx 里接到了 toggleSelectAll —— 但**鼠标 / 触屏
              用户没有任何等价入口**。
              ⇒ 后果:「今天这 20 篇我都要」这种最典型的批量场景,
                纯键盘用户按一下 a 就完事,鼠标用户却只能一篇一篇点。
                改进清单 #8 写的是「长按多选 + 批量复制/收藏」,
                长按进多选后**并没有「全选」这个动作** —— 清单里的
                批量链路缺了最后一环。
              ⇒ 这里补上「全选 / 取消全选」按钮,并:
                 · aria-keyshortcuts="a" —— 与全局快捷键契约一致;
                 · 文案随状态在「全选」/「取消全选」之间切换;
                 · 计数口径用 visibleCount(当前筛选后可见的篇数),
                   与 App.jsx toggleSelectAll 的语义**完全一致**
                   (它就是按 store.filtered 取的)——
                   否则按钮会显示「全选 30 篇」但实际只选中 20 篇。
              放在「复制」**之前**:它是选择类动作,复制类是执行类动作,
              选择在前执行在后符合操作顺序。 */}
          {onSelectAll && (
            <button
              className="batch-bar-btn batch-bar-btn-selectall"
              onClick={onSelectAll}
              disabled={busy || !visibleCount}
              aria-label={allSelected
                ? `取消全选当前可见的 ${visibleCount} 篇`
                : `全选当前可见的 ${visibleCount} 篇`}
              aria-keyshortcuts="a"
              title={allSelected ? '取消全选当前可见（快捷键 A）' : '全选当前可见（快捷键 A）'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {allSelected ? (
                  <>
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </>
                ) : (
                  <>
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M8 12l3 3 5-6"></path>
                  </>
                )}
              </svg>
              <span>{allSelected ? '取消全选' : '全选'}</span>
            </button>
          )}

          <button
            className="batch-bar-btn batch-bar-btn-primary"
            onClick={onCopy}
            disabled={busy || !count}
            aria-label={`复制已选的 ${count} 篇文案到剪贴板`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <span>复制</span>
          </button>

          <button
            className="batch-bar-btn batch-bar-btn-fav"
            onClick={onFavorite}
            disabled={busy || !count}
            aria-label={`把已选的 ${count} 篇加入选材清单（收藏）`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
            </svg>
            <span className="batch-label-long">收藏</span>
            <span className="batch-label-short">收藏</span>
          </button>

          <button
            className="batch-bar-btn batch-bar-btn-unfav"
            onClick={onUnfavorite}
            disabled={busy || !count}
            aria-label={`把已选的 ${count} 篇从选材清单中移除`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              <line x1="4" y1="4" x2="20" y2="20"></line>
            </svg>
            {/* 🔴 T142:桌面用全称「取消收藏」;触屏用短标签「移出」——
                避免与最右侧退出按钮的「取消」重名(重名 ⇒ 误点),
                同时把 4 个字压到 2 个字,320px 放得下。 */}
            <span className="batch-label-long">取消收藏</span>
            <span className="batch-label-short">移出</span>
          </button>

          {/* T-Cron-2026-10-26 03:30 早间 — 改进清单 #5「下载体验」:
              **批量下载封面**。老仓库 App.jsx 有 batchDownloadCovers,
              Vite 重写时丢了 ⇒ 产出链条断一半(文案能批量带走,封面不能)。
              放在「取消收藏」之后、「取消」之前 —— 危险操作(取消收藏)
              在前,下载这种正向操作紧挨着「取消」会被误触。

              🔴 T142:触屏端原来是**纯图标**(span 被 batch.css 隐藏),
                 批量条里三个按钮同形 ⇒ 功能不可发现。
                 ⇒ 桌面「下载封面」(4 字),触屏「封面」(2 字) + ⤓ 图标。 */}
          {onDownloadCovers && (
            <button
              className="batch-bar-btn batch-bar-btn-download"
              onClick={onDownloadCovers}
              disabled={busy || downloading || !count}
              aria-label={`批量下载已选的 ${count} 张封面`}
              title="逐张保存已选内容的封面到下载目录"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 2 0 0 1-2 2H5a2 2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span className="batch-label-long">{downloading ? '下载中…' : '下载封面'}</span>
              <span className="batch-label-short">{downloading ? '下载中' : '封面'}</span>
            </button>
          )}

          <button
            className="batch-bar-cancel"
            onClick={onCancel}
            aria-label="退出批量选择，清空已选"
          >
            取消
          </button>
        </div>
      </div>

      {/* T-Cron-2026-10-27 08:00 早间 — 改进清单 #5「进度条:下载封面进度」。
          批量下载是**十几秒级**的长任务(8 张串行 + 每张 250ms 间隔 +
          网络等待)。以前只有按钮文案变成「下载中…」,用户看不到
          「进行到第几张」⇒ 会以为卡死并重复点。
          这里补一条「N/M 张」进度条。

          为什么用 role="progressbar" 而不是纯 div:
            读屏用户需要听到「第 3 张,共 8 张」才知道任务在推进。
            aria-valuenow/valuemin/valuemax 是 progressbar 的标准契约,
            和 PostDetail 单张下载的 .dl-progress 保持同一套读屏语义。 */}
      {progress && progress.total > 0 && (
        <div className="batch-progress" role="progressbar"
          aria-label="封面批量下载进度"
          aria-valuenow={progress.done}
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuetext={`正在下载第 ${Math.min(progress.done + 1, progress.total)} 张,共 ${progress.total} 张`}>
          <div className="batch-progress-head">
            <span className="batch-progress-label">
              正在下载 {progress.done} / {progress.total} 张
            </span>
            {progress.failed > 0 && (
              <span className="batch-progress-failed">{progress.failed} 张失败</span>
            )}
          </div>
          <div className="batch-progress-track">
            <div className="batch-progress-fill"
              style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}></div>
          </div>
        </div>
      )}

      {/* 操作结果播报:读屏用户需要听到"已复制 3 篇"才知道成功了。
          T-Cron-2026-10-31:换成共享 DismissibleToast —— 支持点击 / Esc 关闭,
          且 tabIndex=0 让键盘用户能主动关掉挡住卡片的提示条。 */}
      <DismissibleToast
        message={toast}
        onDismiss={() => {
          setToast('');
          if (toastTimer.current) clearTimeout(toastTimer.current);
        }}
      />
    </>
  );
}

window.BatchActionBar = BatchActionBar;

export default BatchActionBar;
